/**
 * IM v2.0 — 飞书风格视觉地基（commit 0）
 *
 * 严格按"零节设计宪法"实现：
 * - 颜色 token / 圆角 / 间距 / 字重全部走 im.css
 * - 消息气泡：自他蓝/灰双色 + 朝头像角 4px 其余 16px + 无 shadow
 * - 头像：8 色 hash 描边 + 同人连续 5min 合并 + 后续条占位隐藏
 * - @# chip：胶囊样式（mention-bg / biz-bg）
 * - 三栏：56px 应用导航（深色）+ 280 列表 + 主区 + 320 详情，0.5px 分割
 * - 输入框：focus 边框变蓝无阴影 + 工具栏 + 草稿
 * - 反模式自检：无 box-shadow / 无 600 字重 / 瞬时滚动到底 / no spring
 *
 * 功能保留 IM v1 全部 27 项（图片/文件/编辑/转发/收藏/置顶/搜索/通知…），
 * 本 commit 仅做视觉重构 — 业务逻辑零删除。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { io, Socket } from 'socket.io-client';
import dayjs from 'dayjs';
import { Input, Modal, message as antdMsg, Image as AntdImage, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  IconSearch, IconPlus, IconBell,
  IconChecks, IconCheck, IconClock, IconAlertCircle, IconUsers,
  IconBellOff, IconPin, IconAt, IconHash, IconMoodSmile, IconPaperclip,
  IconPhoto, IconScreenshot, IconMicrophone, IconCheckbox, IconSend,
  IconDots, IconX, IconSpeakerphone, IconUser, IconMessageCircle,
} from '@tabler/icons-react';
import { $, api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import { PageHeader, Btn, Avatar as UiAvatar, avatarSlot } from '../components/ui';
import { fmtDate, fmtTime, fmtMonthDayTime } from '../lib/format';

interface Conversation {
  id: number; type: string; name: string;
  avatar?: string | null;
  avatarText?: string | null;     // AI 生成的群代表字 1-2 字
  avatarColorIndex?: number | null;
  last_preview?: string; last_from?: number; last_from_name?: string | null;
  last_type?: string;
  last_message_at?: string; unread: number; muted: number;
  is_pinned?: boolean; has_at_me?: boolean;
  draft_content?: string | null;
  cleared_before_message_id?: number | null;
  my_role?: string;
  linked_entity_type?: string; linked_entity_id?: number;
  partner?: { id: number; realName: string; avatarUrl: string | null };
}
interface Attachment {
  fileUrl: string; fileName?: string | null;
  fileSize?: number | null; mimeType?: string | null;
  thumbnailUrl?: string | null;
}
interface Message {
  id: number; conversationId: number; fromUserId: number | null;
  content: string; contentType: string;
  mentionedUserIds?: number[];
  linkedEntityType?: string; linkedEntityId?: number;
  replyToMessageId?: number;
  quickActionsJson?: Array<{ id: string; label: string; kind?: string }>;
  recalledAt?: string | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  forwardedFromMessageId?: number | null;
  deliveryStatus: string;
  createdAt: string;
  senderRealName?: string;
  senderAvatarUrl?: string | null;
  attachment?: Attachment;
  isStarred?: boolean;
}

// v1.2-rc2：WS 走相对 namespace 'path'，base 自动 = window.location.origin
// → prod 同源 wss://<host>/socket.io/，dev 走 vite proxy（vite.config.ts ws:true）
const SAME_SENDER_WINDOW_MS = 5 * 60 * 1000;  // 同人 5 分钟内合并

/* ────────── 头像（im.css 类） ────────── */
function Avatar({
  userId, src, name, size = 'md', online,
}: {
  userId: number | string;
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  online?: boolean;
}) {
  const [err, setErr] = useState(false);
  const slot = avatarSlot(userId);
  const first = (name ?? '?').slice(0, 1);
  return (
    <div className={`im-avatar size-${size} c${slot}`}>
      {src && !err ? <img src={src} alt={name ?? ''} onError={() => setErr(true)} /> : first}
      {online && <span className="online-dot" />}
    </div>
  );
}

/* ────────── 智能时间格式（3.4 + 4.4） ────────── */
function smartTime(s?: string): string {
  if (!s) return '';
  const d = dayjs(s);
  const now = dayjs();
  if (d.isSame(now, 'day')) return d.format('HH:mm');
  if (d.isSame(now.subtract(1, 'day'), 'day')) return '昨天';
  if (d.isAfter(now.subtract(6, 'day'))) {
    const wd = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'][d.day()];
    return wd;
  }
  return d.format('MM-DD');
}

function previewText(c: Conversation): { node: any } {
  if (c.draft_content?.trim()) {
    return { node: <><span className="im-conv-preview-prefix-draft">[草稿]</span> {c.draft_content.slice(0, 30)}</> };
  }
  if (c.has_at_me && c.unread > 0) {
    const base = previewBase(c);
    return { node: <><span className="im-conv-preview-prefix-at">[有人@我]</span> {base}</> };
  }
  return { node: previewBase(c) };
}
/* ────────── 语音消息识别（手机 App 发 voice-{N}s-{时间戳}.m4a，网页端渲染成可播语音条；后端/手机端不动）────────── */
const VOICE_NAME_RE = /voice-(\d+)s-\d+\.m4a$/i;
function isVoiceAtt(att?: Attachment | null): boolean {
  if (!att) return false;
  return VOICE_NAME_RE.test(att.fileName ?? '') || !!att.mimeType?.startsWith('audio/');
}
function parseVoiceSeconds(name?: string | null): number | null {
  const m = (name ?? '').match(VOICE_NAME_RE);
  return m ? Number(m[1]) : null;
}

function previewBase(c: Conversation): string {
  const t = c.last_type;
  const base = t === 'image' ? '[图片]'
             : t === 'file' ? (parseVoiceSeconds(c.last_preview) != null ? `[语音] ${parseVoiceSeconds(c.last_preview)}"` : `[文件] ${c.last_preview ?? ''}`)
             : t === 'notification' ? c.last_preview ?? '[系统消息]'
             : c.last_preview ?? '暂无消息';
  if (c.last_from_name && c.type !== 'direct' && c.type !== 'system_notification') {
    return `${c.last_from_name}: ${base}`;
  }
  return base;
}

/* ════════════════════════════════════════════════════════════════
 * Main
 * ════════════════════════════════════════════════════════════════ */
export function MessengerPage() {
  const token = useAuthStore((s) => s.accessToken);
  const me = useAuthStore((s) => s.user)!;
  const qc = useQueryClient();
  const [activeConvId, setActiveConvId] = useState<number | null>(null);
  const [wsStatus, setWsStatus] = useState<'connecting' | 'online' | 'offline'>('connecting');
  const [detailOpen, setDetailOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  // P0-2：搜索结果跳转，记录目标消息 id 由 ChatArea 跳转 + 高亮
  const [anchorMessageId, setAnchorMessageId] = useState<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [typingByConv, setTypingByConv] = useState<Record<number, { userId: number; ts: number }>>({});

  const { data: convs } = useQuery<Conversation[]>({
    queryKey: ['im-conversations'],
    queryFn: () => $.get('/im/conversations'),
    refetchInterval: 15_000,
  });

  // im12：document.title 未读前缀 (N) 已提升到全局（useImRealtime 按合并未读设、任何页生效），
  //   此处页级写法（含仅服务于它的 totalUnread useMemo）删除，避免与全局双写互相覆盖。

  // 桌面通知已移除（反应慢 + 干扰）
  // 失焦时改靠：标题闪烁 (N) + 顶栏铃铛红点 + 提示音 — 已实现

  // Ctrl+K
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // 自动选会话
  useEffect(() => {
    if (!activeConvId && convs && convs.length > 0) {
      const urlConvId = new URLSearchParams(window.location.search).get('conv');
      if (urlConvId && convs.some((c) => c.id === Number(urlConvId))) {
        setActiveConvId(Number(urlConvId));
      } else {
        setActiveConvId(convs[0].id);
      }
    }
  }, [convs, activeConvId]);

  // WS
  useEffect(() => {
    if (!token) return;
    const socket = io('/im', {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true, reconnectionDelay: 1000, reconnectionDelayMax: 30000,
    });
    socketRef.current = socket;
    socket.on('connect', () => { console.log('[ws] connected', socket.id); setWsStatus('online'); });
    socket.on('disconnect', (reason: string) => { console.log('[ws] disconnect', reason); setWsStatus('offline'); });
    socket.on('connect_error', (e: any) => { console.log('[ws] connect_error', e?.message); setWsStatus('offline'); });
    socket.on('message:new', (m: Message) => {
      console.log('[ws] message:new', m);
      const cid = Number(m.conversationId);
      // ★ P0-1+：用 setQueriesData + predicate，跨 type 兼容（conv.id 可能 string）
      // 之前 setQueryData(['im-messages', cid]) 如果 cache key 是 string "5" 而 cid 是 number 5
      // 不命中，导致"已打开 pane 不刷新"假绿
      qc.setQueriesData<Message[]>(
        {
          queryKey: ['im-messages'],
          predicate: (q) => Number((q.queryKey as any[])[1]) === cid,
        },
        (old) => {
          if (!old) return old;
          if (old.some((x) => Number(x.id) === Number(m.id))) return old;
          return [...old, m];
        },
      );
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      // 实时同步左侧「消息」菜单角标（=IM私信+通知合并数，在 Layout）：新消息即刻 +1，不等 30s 轮询。
      qc.invalidateQueries({ queryKey: ['im-unread-total'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread'] }); // 通知类消息(contentType=notification)也同步铃铛/合并数
      // 提示音已全局化(快批-1)：统一由 Layout 未读涨值检测处 notificationSound.play()（非 /im 页响、全局1s debounce）。
      //   /im 页内用户正在看消息(socket 即时刷新消息体)，不另响声音，与全局 toast 抑制同口径。
      // 桌面通知已移除；失焦提示走 标题(N)闪 + 顶栏铃铛 + 全局提示音。
    });
    socket.on('message:edit', (e: { id: number; content: string; editedAt: string }) => {
      console.log('[ws] message:edit', e);
      qc.setQueriesData<Message[]>({ queryKey: ['im-messages'] }, (old) => {
        if (!old) return old;
        return old.map((m) => Number(m.id) === Number(e.id) ? { ...m, content: e.content, editedAt: e.editedAt } : m);
      });
    });
    socket.on('message:delete', (e: { id: number }) => {
      console.log('[ws] message:delete', e);
      qc.setQueriesData<Message[]>({ queryKey: ['im-messages'] }, (old) => {
        if (!old) return old;
        return old.map((m) => Number(m.id) === Number(e.id) ? { ...m, deletedAt: new Date().toISOString() } : m);
      });
    });
    socket.on('message:recall', (e: { id: number }) => {
      console.log('[ws] message:recall', e);
      qc.setQueriesData<Message[]>({ queryKey: ['im-messages'] }, (old) => {
        if (!old) return old;
        return old.map((m) => Number(m.id) === Number(e.id) ? { ...m, recalledAt: new Date().toISOString() } : m);
      });
      // P1-10：撤回后侧栏 preview 也要刷新
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
    });
    // P0-9 已读回执：发送方收到对方已读 → 单勾变蓝双勾
    socket.on('message:read', (e: { ids: number[] }) => {
      const ids = new Set((e.ids ?? []).map(Number));
      if (!ids.size) return;
      qc.setQueriesData<Message[]>({ queryKey: ['im-messages'] }, (old) => {
        if (!old) return old;
        return old.map((m) => ids.has(Number(m.id)) ? { ...m, deliveryStatus: 'read' } : m);
      });
    });
    socket.on('conv:update', () => {
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      qc.invalidateQueries({ queryKey: ['im-conv-detail'] });
    });
    // ★ P0-3：被邀请进新群 / 新私聊 → 侧栏立即出现
    socket.on('conversation:created', (conv: any) => {
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      if (Number(conv?.createdByUserId) !== Number(me.id)) {
        antdMsg.info(`你被加入会话「${conv?.name ?? '新会话'}」`);
      }
    });
    // v1.8 项2 群解散：从列表移除 + 若正打开被解散会话则回空状态
    socket.on('conversation:dissolved', (e: { convId: number }) => {
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      setActiveConvId((prev) => (Number(prev) === Number(e.convId) ? null : prev));
      antdMsg.success('群聊已解散');
    });
    socket.on('message:typing', (e: { userId: number; convId: number }) => {
      if (Number(e.userId) === Number(me.id)) return;
      setTypingByConv((m) => ({ ...m, [e.convId]: { userId: e.userId, ts: Date.now() } }));
    });
    return () => { socket.close(); };
  }, [token, qc, me.id]);

  useEffect(() => {
    const t = setInterval(() => {
      setTypingByConv((m) => {
        const now = Date.now();
        const next: any = {};
        for (const k in m) if (now - m[k].ts < 3000) next[k] = m[k];
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const activeConv = convs?.find((c) => c.id === activeConvId);

  return (
    <>
      <PageHeader
        breadcrumb={['消息']}
        title="消息"
        subtitle={wsStatus === 'online' ? '在线' : wsStatus === 'connecting' ? '连接中' : '离线'}
        actions={
          <Btn kind="default" onClick={() => setGlobalSearchOpen(true)}><IconSearch size={13} style={{ verticalAlign: '-2px' }} /> 搜索 (Ctrl+K)</Btn>
        }
      />
      {wsStatus === 'offline' && (
        <div style={{
          padding: '8px 16px', background: 'var(--im-warning-bg)',
          color: 'var(--im-warning)', fontSize: 13,
        }}>
          ⚠️ 网络已断开，正在重连…
        </div>
      )}
      <div className="im-shell">
        <ConversationList
          convs={convs ?? []}
          activeId={activeConvId}
          onSelect={(id: number) => {
            setActiveConvId(id);
            // 点击立即清未读（乐观，等 ChatArea 内 markRead 真正持久化）
            qc.setQueryData<Conversation[]>(['im-conversations'], (old) => {
              if (!old) return old;
              return old.map((c) =>
                Number(c.id) === Number(id) ? { ...c, unread: 0, has_at_me: false } : c,
              );
            });
          }}
          typingByConv={typingByConv}
        />
        {activeConv ? (
          <ChatArea
            key={activeConv.id}
            conv={activeConv}
            me={me}
            socket={socketRef.current}
            onToggleDetail={() => setDetailOpen((v) => !v)}
            detailOpen={detailOpen}
            typingUserId={typingByConv[activeConv.id]?.userId}
            anchorMessageId={anchorMessageId}
            onAnchorConsumed={() => setAnchorMessageId(null)}
          />
        ) : (
          <EmptyState />
        )}
        {detailOpen && activeConv && <DetailDrawer conv={activeConv} me={me} />}
      </div>
      <GlobalSearchModal
        open={globalSearchOpen}
        onClose={() => setGlobalSearchOpen(false)}
        onJump={(convId: number, messageId: number) => {
          setActiveConvId(convId);
          setAnchorMessageId(messageId);
          setGlobalSearchOpen(false);
        }}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 会话列表 — 3 节
 * ════════════════════════════════════════════════════════════════ */
/** 项1：系统会话名固定文案（不显示 DB 存的 emoji，也不正则 strip 以免误伤用户群名里的 emoji） */
function convDisplayName(c: any): string {
  if (c?.type === 'system_notification') return '系统通知';
  if (c?.type === 'company_broadcast') return '全员广播';
  return c?.name || (c?.type === 'direct' ? '私聊' : '未命名');
}

function ConversationList({ convs, activeId, onSelect, typingByConv }: any) {
  const me = useAuthStore((s) => s.user)!;
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const qc = useQueryClient();
  const pinToggle = useMutation({
    mutationFn: (id: number) => $.post(`/im/conversations/${id}/pin-toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['im-conversations'] }),
  });
  const muteToggle = useMutation({
    mutationFn: (id: number) => $.post(`/im/conversations/${id}/mute-toggle`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['im-conversations'] }),
  });
  const clearConv = useMutation({
    mutationFn: (id: number) => $.post(`/im/conversations/${id}/clear`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
      qc.invalidateQueries({ queryKey: ['im-messages'] });
      antdMsg.success('已清空');
    },
  });
  const leaveConv = useMutation({
    mutationFn: (id: number) => $.post(`/im/conversations/${id}/leave`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-conversations'] }); antdMsg.success('已退出'); },
    onError: (e: any) => antdMsg.error(e?.message ?? '退出失败'),
  });
  // 项1 删除会话（软隐藏当前用户视图，不影响他人）
  const deleteConv = useMutation({
    mutationFn: (id: number) => api.delete(`/im/conversations/${id}`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-conversations'] }); antdMsg.success('已删除会话'); },
    onError: (e: any) => antdMsg.error(e?.response?.data?.message ?? '删除失败'),
  });
  // 项2 解散群（群主 / admin / director）
  const dissolveGroup = useMutation({
    mutationFn: (id: number) => $.post(`/im/conversations/${id}/dissolve`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-conversations'] }); /* toast 统一由 WS conversation:dissolved 弹，避免重复 */ },
    onError: (e: any) => antdMsg.error(e?.message ?? '解散失败'),
  });

  // 3.6 排序：置顶 > 普通，组内按 last_message_at 倒序（后端已 ORDER BY，FE 保留）
  const filtered = (convs as Conversation[]).filter((c) => !search.trim() || (c.name ?? '').toLowerCase().includes(search.toLowerCase()));

  return (
    <aside className="im-conv-pane">
      <div className="im-conv-search">
        <div className="im-conv-search-box">
          <IconSearch size={14} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜索"
          />
        </div>
        <button className="im-conv-add" onClick={() => setShowNew(true)} title="新建会话">
          <IconPlus size={16} />
        </button>
      </div>
      <div className="im-conv-list">
        {filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--im-text-tertiary)', fontSize: 13 }}>
            还没有任何对话<br />点上方 + 按钮新建会话
          </div>
        ) : filtered.map((c) => {
          const active = c.id === activeId;
          const isDirect = c.type === 'direct' && c.partner;
          const isTyping = typingByConv[c.id];
          const canDissolve = (c.type === 'group' || c.type === 'team_group')
            && (c.my_role === 'owner' || ['admin', 'director'].includes(me.role));
          const menu: MenuProps['items'] = [
            { key: 'pin', label: c.is_pinned ? '取消置顶' : '置顶', onClick: () => pinToggle.mutate(c.id) },
            { key: 'mute', label: c.muted ? '取消静音' : '静音', onClick: () => muteToggle.mutate(c.id) },
            // ux1：IM 菜单危险操作（清空/删除/退出/解散）window.confirm 后加 isPending guard 防重，避免连点重复 post
            { key: 'clear', label: '清空聊天记录',
              onClick: () => { if (!clearConv.isPending && window.confirm('确认清空？仅本人侧不可见，对方仍可见')) clearConv.mutate(c.id); } },
            { key: 'delete', label: '删除会话', danger: true,
              onClick: () => { if (!deleteConv.isPending && window.confirm('删除会话？仅从你的列表移除、不影响对方；对方再发消息会重新出现')) deleteConv.mutate(c.id); } },
            ...(c.type === 'group' && c.my_role !== 'owner' ? [{
              key: 'leave', label: '退出群聊', danger: true,
              onClick: () => { if (!leaveConv.isPending && window.confirm('确认退出？')) leaveConv.mutate(c.id); },
            }] : []),
            ...(canDissolve ? [{
              key: 'dissolve', label: '解散群聊', danger: true,
              onClick: () => { if (!dissolveGroup.isPending && window.confirm('确认解散？群将被删除、全员移出，不可恢复')) dissolveGroup.mutate(c.id); },
            }] : []),
          ];
          const preview = previewText(c);
          return (
            <Dropdown key={c.id} menu={{ items: menu }} trigger={['contextMenu']}>
              <div
                className={`im-conv-item${active ? ' active' : ''}`}
                onClick={() => onSelect(c.id)}
              >
                <div className="im-conv-avatar-wrap">
                  {isDirect ? (
                    <Avatar userId={c.partner!.id} src={c.partner!.avatarUrl}
                            name={c.partner!.realName} size="lg" />
                  ) : c.avatar ? (
                    <div className="im-avatar size-lg"><img src={c.avatar} alt="" /></div>
                  ) : (
                    <div className={`im-avatar size-lg c${(c.avatarColorIndex ?? avatarSlot(c.id)) % 8 + 1}`}
                         style={{ fontSize: (c.avatarText && c.avatarText.length === 2) ? 13 : 16 }}>
                      {c.type === 'system_notification' ? <IconBell size={18} />
                        : c.type === 'company_broadcast' ? <IconSpeakerphone size={18} />
                        : c.avatarText
                          ? c.avatarText
                          : (c.type === 'group' || c.type === 'team_group')
                            ? <IconUsers size={18} />
                            : (c.name ?? '?').slice(0, 1)}
                    </div>
                  )}
                  {c.unread > 0 && (
                    <span className={`im-conv-unread${c.muted ? ' muted' : ''}`}>
                      {c.muted ? '' : (c.unread > 99 ? '99+' : c.unread)}
                    </span>
                  )}
                </div>
                <div className="im-conv-body">
                  <div className="im-conv-name-row">
                    <div className="im-conv-name">
                      {c.is_pinned && <IconPin size={11} className="im-conv-pin-mark" />}
                      <span className="im-conv-name-text">{convDisplayName(c)}</span>
                      {(c.type === 'group' || c.type === 'team_group') && <IconUsers size={12} className="im-conv-icon-tag" />}
                      {c.muted ? <IconBellOff size={12} className="im-conv-mute-mark" /> : null}
                    </div>
                    {c.last_message_at && (
                      <span className="im-conv-time">{smartTime(c.last_message_at)}</span>
                    )}
                  </div>
                  <div className="im-conv-preview-row">
                    {isTyping ? <span className="im-conv-typing">正在输入…</span> : preview.node}
                  </div>
                </div>
              </div>
            </Dropdown>
          );
        })}
      </div>
      <NewConversationModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onDone={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['im-conversations'] }); }}
      />
    </aside>
  );
}

/* ────────── 新建会话 ────────── */
function NewConversationModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: (id?: number) => void }) {
  const me = useAuthStore((s) => s.user)!;
  const [tab, setTab] = useState<'direct' | 'group' | 'broadcast'>('direct');
  const [query, setQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [groupName, setGroupName] = useState('');
  const [broadcastText, setBroadcastText] = useState('');
  const { data: users } = useQuery<any[]>({
    queryKey: ['im-searchable-users', query],
    queryFn: () => $.get(`/im/users/searchable?q=${encodeURIComponent(query)}`),
    enabled: open,
  });
  const createDirect = useMutation({
    mutationFn: (toUserId: number) => $.post('/im/conversations/direct', { to_user_id: toUserId }),
    onSuccess: (r: any) => { antdMsg.success('直接会话已创建'); onDone(r.id); },
    onError: (e: any) => antdMsg.error(e?.message ?? '创建失败'),
  });
  const createGroup = useMutation({
    mutationFn: () => $.post('/im/conversations/group', { name: groupName, member_ids: selectedUsers }),
    onSuccess: (r: any) => { antdMsg.success('群聊已创建'); onDone(r.id); reset(); },
    onError: (e: any) => antdMsg.error(e?.message ?? '创建失败'),
  });
  const sendBroadcast = useMutation({
    mutationFn: () => $.post('/im/broadcast', { content: broadcastText }),
    onSuccess: (r: any) => {
      antdMsg.success(`已发送 · ${r.sent} 人收到`);
      onDone(); setBroadcastText('');
    },
    onError: (e: any) => antdMsg.error(e?.message ?? '广播失败'),
  });
  const reset = () => { setGroupName(''); setSelectedUsers([]); setQuery(''); };
  const tabs: any[] = [
    { key: 'direct', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconUser size={14} /> 找人聊</span> },
    { key: 'group', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconUsers size={14} /> 创建群</span> },
  ];
  if (['admin', 'director'].includes(me.role)) tabs.push({ key: 'broadcast', label: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><IconSpeakerphone size={14} /> 全员广播</span> });

  return (
    <Modal title="新建会话" open={open} onCancel={() => { reset(); onClose(); }} footer={null} width={520}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '0.5px solid var(--im-border-light)' }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 12px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: tab === t.key ? 500 : 400,
            color: tab === t.key ? 'var(--im-primary)' : 'var(--im-text-secondary)',
            borderBottom: tab === t.key ? '2px solid var(--im-primary)' : '2px solid transparent',
            transition: 'color 100ms linear, border-color 240ms cubic-bezier(0,0,0.2,1)',
          }}>{t.label}</button>
        ))}
      </div>
      {tab === 'direct' && (
        <>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索同事姓名" style={{ marginBottom: 12 }} />
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {(users ?? []).map((u: any) => (
              <div key={u.id} className="im-picker-item" onClick={() => createDirect.mutate(u.id)}>
                <Avatar userId={u.id} src={u.avatarUrl} name={u.realName} size="md" />
                <div style={{ flex: 1 }}>
                  <div className="im-picker-name">{u.realName}</div>
                  <div style={{ fontSize: 11, color: 'var(--im-text-tertiary)' }}>{u.role}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {tab === 'group' && (
        <>
          <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="群名称（选填，留空自动用成员名）" style={{ marginBottom: 12 }} />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索成员" style={{ marginBottom: 8 }} />
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '0.5px solid var(--im-border-light)', borderRadius: 8 }}>
            {/* 项5 创建者(我)默认勾上、不可取消；项4 按钮计数 N+1 因此合理 */}
            <div className="im-picker-item" style={{ opacity: 0.85 }}>
              <input type="checkbox" checked disabled />
              <Avatar userId={me.id} src={me.avatarUrl} name={me.realName} size="sm" />
              <span>{me.realName}（我）</span>
              <span style={{ fontSize: 11, color: 'var(--im-primary)', marginLeft: 'auto' }}>群主</span>
            </div>
            {(users ?? []).filter((u: any) => Number(u.id) !== Number(me.id)).map((u: any) => {
              const sel = selectedUsers.includes(u.id);
              return (
                <div key={u.id} className="im-picker-item"
                     style={sel ? { background: 'var(--im-mention-bg)' } : undefined}
                     onClick={() => setSelectedUsers(sel ? selectedUsers.filter((x) => x !== u.id) : [...selectedUsers, u.id])}>
                  <input type="checkbox" checked={sel} readOnly />
                  <Avatar userId={u.id} src={u.avatarUrl} name={u.realName} size="sm" />
                  <span>{u.realName}</span>
                  <span style={{ fontSize: 11, color: 'var(--im-text-tertiary)', marginLeft: 'auto' }}>{u.role}</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12 }}>
            <Btn kind="primary" onClick={() => createGroup.mutate()}
                 disabled={selectedUsers.length === 0 || createGroup.isPending}>
              {createGroup.isPending ? '创建中…' : `创建群聊（${selectedUsers.length + 1} 人）`}
            </Btn>
          </div>
        </>
      )}
      {tab === 'broadcast' && (
        <>
          <div style={{ marginBottom: 12, padding: 10, background: 'var(--im-warning-bg)', borderRadius: 8, fontSize: 12, color: 'var(--im-warning)' }}>
            ⚠️ 全员广播会发送到所有 active 用户的系统通知会话
          </div>
          <Input.TextArea value={broadcastText} onChange={(e) => setBroadcastText(e.target.value)}
                          rows={6} placeholder="广播内容" style={{ marginBottom: 12 }} />
          <Btn kind="primary" onClick={() => sendBroadcast.mutate()}
               disabled={!broadcastText.trim() || sendBroadcast.isPending}>
            {sendBroadcast.isPending ? '发送中…' : '发送给全员'}
          </Btn>
        </>
      )}
    </Modal>
  );
}

/* ════════════════════════════════════════════════════════════════
 * 聊天主区
 * ════════════════════════════════════════════════════════════════ */
function ChatArea({ conv, me, socket, onToggleDetail, detailOpen, typingUserId, anchorMessageId, onAnchorConsumed }: any) {
  const qc = useQueryClient();
  const listRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState(conv.draft_content ?? '');
  const [quotedMessage, setQuotedMessage] = useState<Message | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [entityOpen, setEntityOpen] = useState(false);
  const [entityQuery, setEntityQuery] = useState('');
  const [showInConvSearch, setShowInConvSearch] = useState(false);
  const [readsModal, setReadsModal] = useState<Message | null>(null);
  const [forwardModal, setForwardModal] = useState<Message | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');
  // P1-6 多选模式 + 翻译缓存 + 举报弹框
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [translationByMsg, setTranslationByMsg] = useState<Record<number, string>>({});
  const [reportTarget, setReportTarget] = useState<Message | null>(null);
  // commit 6: 顶部 Tab + 未读分割线 + 新消息浮按钮
  const [topTab, setTopTab] = useState<'messages' | 'files' | 'images' | 'pin'>('messages');
  const lastReadIdRef = useRef<number | null>(null);
  const [newMsgFloat, setNewMsgFloat] = useState(0);
  const [scrolledUp, setScrolledUp] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setText(conv.draft_content ?? ''); }, [conv.id, conv.draft_content]);

  const { data: messages } = useQuery<Message[]>({
    queryKey: ['im-messages', conv.id],
    queryFn: () => $.get(`/im/conversations/${conv.id}/messages?limit=80`),
  });
  const { data: pins } = useQuery<any[]>({
    queryKey: ['im-pins', conv.id],
    queryFn: () => $.get(`/im/conversations/${conv.id}/pins`),
  });

  // 记录进入会话时的 last_read（用来定位"以下是新消息"分割线）
  useEffect(() => {
    lastReadIdRef.current = (conv as any).last_read_message_id ?? null;
    setNewMsgFloat(0);
    setScrolledUp(false);
  }, [conv.id]);

  // 瞬时滚动到底（V17）— 但 anchorMessageId 存在或用户上滚时不强制滚
  useEffect(() => {
    if (anchorMessageId) return;
    if (!listRef.current || !messages) return;
    if (scrolledUp) {
      // 用户在上方，统计有几条新增消息触发浮按钮
      setNewMsgFloat((prev) => prev + 1);
      return;
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, anchorMessageId]);

  // 监听滚动 — 用户离底 >100px 视为上滚状态
  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setScrolledUp(!nearBottom);
    if (nearBottom) setNewMsgFloat(0);
  };
  const scrollToBottomNow = () => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
    setScrolledUp(false);
    setNewMsgFloat(0);
  };

  // P0-2 锚定跳转 + 高亮 800ms
  useEffect(() => {
    if (!anchorMessageId || !messages || !listRef.current) return;
    const found = messages.some((m) => Number(m.id) === Number(anchorMessageId));
    if (!found) return;
    // 等 DOM 渲染完成
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-msg-id="${anchorMessageId}"]`) as HTMLElement | null;
      if (el && listRef.current) {
        el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' });
        el.classList.add('im-msg-highlight');
        setTimeout(() => el.classList.remove('im-msg-highlight'), 800);
      }
      onAnchorConsumed?.();
    });
  }, [anchorMessageId, messages, onAnchorConsumed]);

  // 标记已读 + 乐观清未读
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    console.log('[conv] markRead', { convId: conv.id, lastMsgId: last.id });
    $.post(`/im/conversations/${conv.id}/read`, { message_id: last.id })
      .then(() => console.log('[conv] read OK'))
      .catch((e) => console.log('[conv] read err', e));
    // 乐观更新：立即清未读 + has_at_me（不等 15s 轮询）
    qc.setQueryData<Conversation[]>(['im-conversations'], (old) => {
      if (!old) return old;
      return old.map((c) =>
        Number(c.id) === Number(conv.id)
          ? { ...c, unread: 0, has_at_me: false }
          : c,
      );
    });
    // 已读后即刻刷新左侧「消息」菜单角标(合并数)，不等 30s 轮询。
    qc.invalidateQueries({ queryKey: ['im-unread-total'] });
  }, [conv.id, messages, qc]);

  // 草稿自动保存
  useEffect(() => {
    const t = setTimeout(() => {
      if (text !== (conv.draft_content ?? '')) {
        $.put(`/im/conversations/${conv.id}/draft`, { content: text || null }).catch(() => {});
      }
    }, 500);
    return () => clearTimeout(t);
  }, [text, conv.id, conv.draft_content]);

  const send = useMutation({
    mutationFn: () => $.post(`/im/conversations/${conv.id}/messages`, {
      content: text,
      reply_to_message_id: quotedMessage?.id,
      mentioned_user_ids: extractMentionIds(text),
    }),
    onSuccess: () => {
      setText(''); setQuotedMessage(null);
      // P1-4：立即清草稿 — backend 异步 + FE 乐观更新
      $.put(`/im/conversations/${conv.id}/draft`, { content: null }).catch(() => {});
      qc.setQueryData<Conversation[]>(['im-conversations'], (old) => {
        if (!old) return old;
        return old.map((c) => c.id === conv.id ? { ...c, draft_content: null } : c);
      });
      qc.invalidateQueries({ queryKey: ['im-messages', conv.id] });
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
    },
    onError: (e: any) => antdMsg.error(e?.message ?? '发送失败'),
  });
  const handleSend = () => { if (text.trim() && !send.isPending) send.mutate(); };

  const recall = useMutation({
    mutationFn: (id: number) => $.post(`/im/messages/${id}/recall`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-messages', conv.id] }); antdMsg.success('已撤回'); },
    onError: (e: any) => antdMsg.error(e?.message ?? '撤回失败'),
  });
  const delMsg = useMutation({
    mutationFn: (id: number) => api.delete(`/im/messages/${id}`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-messages', conv.id] }); antdMsg.success('已删除'); },
  });
  const editMutation = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      api.patch(`/im/messages/${id}`, { content }).then((r) => r.data),
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ['im-messages', conv.id] });
    },
    onError: (e: any) => antdMsg.error(e?.response?.data?.message ?? '编辑失败'),
  });
  const star = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? $.post(`/im/messages/${id}/star`, {})
         : api.delete(`/im/messages/${id}/star`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['im-messages', conv.id] }),
  });
  const pin = useMutation({
    mutationFn: ({ id, on }: { id: number; on: boolean }) =>
      on ? $.post(`/im/conversations/${conv.id}/pins/${id}`, {})
         : api.delete(`/im/conversations/${conv.id}/pins/${id}`).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-pins', conv.id] }); antdMsg.success('OK'); },
  });

  // P1-6 翻译
  const translateMutation = useMutation({
    mutationFn: ({ id, lang }: { id: number; lang: string }) =>
      $.post<any>(`/im/messages/${id}/translate`, { target_lang: lang }),
    onSuccess: (r: any, vars) => {
      setTranslationByMsg((m) => ({ ...m, [vars.id]: r.translated ?? '' }));
    },
    onError: (e: any) => antdMsg.error(e?.message ?? '翻译失败'),
  });
  const reportMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      $.post(`/im/messages/${id}/report`, { reason }),
    onSuccess: () => { antdMsg.success('已举报，管理员将处理'); setReportTarget(null); },
    onError: (e: any) => antdMsg.error(e?.message ?? '举报失败'),
  });
  const batchForward = (msgIds: number[]) => setForwardModal({ id: msgIds[0], conversationId: conv.id } as any);
  const batchDelete = async (msgIds: number[]) => {
    if (!window.confirm(`确认删除 ${msgIds.length} 条？仅本人侧不可见`)) return;
    await Promise.all(msgIds.map((id) => api.delete(`/im/messages/${id}`).catch(() => null)));
    setSelectedIds(new Set());
    setSelectMode(false);
    qc.invalidateQueries({ queryKey: ['im-messages', conv.id] });
    antdMsg.success('已删除');
  };

  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/im/conversations/${conv.id}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60_000,  // A3：附件跨云上传慢，覆盖全局 axios 30s（后端 OSS adapter 同步放宽到 60s）
      });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['im-messages', conv.id] }),
    onError: (e: any) => antdMsg.error(e?.response?.data?.message ?? '上传失败'),
  });

  const [dragOver, setDragOver] = useState(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    for (const f of files) uploadFile.mutate(f);
  };

  // 粘贴上传
  useEffect(() => {
    const h = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('.im-input-area') && !target?.closest('.im-msg-list')) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            uploadFile.mutate(new File([f], `paste-${Date.now()}.png`, { type: f.type }));
          }
        }
      }
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
  }, []);

  const lastTypingSent = useRef(0);
  const onInput = (v: string) => {
    setText(v);
    const m = v.match(/@([一-龥\w]*)$/);
    if (m) { setMentionOpen(true); setMentionQuery(m[1]); }
    else setMentionOpen(false);
    const e = v.match(/#([一-龥\w]*)$/);
    if (e) { setEntityOpen(true); setEntityQuery(e[1]); }
    else setEntityOpen(false);
    if (socket && Date.now() - lastTypingSent.current > 2000) {
      lastTypingSent.current = Date.now();
      socket.emit('message:typing', { conv_id: conv.id });
    }
  };

  return (
    <section className="im-chat-pane">
      {/* 顶栏 — 4.1 */}
      <div className="im-chat-header">
        <div className="im-chat-header-info">
          {conv.type === 'direct' && conv.partner ? (
            <Avatar userId={conv.partner.id} src={conv.partner.avatarUrl} name={conv.partner.realName} size="md" />
          ) : conv.avatar ? (
            <div className="im-avatar size-md"><img src={conv.avatar} alt="" /></div>
          ) : (
            <div className={`im-avatar size-md c${(conv.avatarColorIndex ?? avatarSlot(conv.id)) % 8 + 1}`}
                 style={{ fontSize: (conv.avatarText && conv.avatarText.length === 2) ? 12 : 14 }}>
              {conv.type === 'system_notification' ? <IconBell size={18} />
                : conv.avatarText
                  ? conv.avatarText
                  : conv.type === 'group' || conv.type === 'team_group'
                    ? <IconUsers size={18} />
                    : (conv.name ?? '?').slice(0, 1)}
            </div>
          )}
          <div>
            <div className="im-chat-header-title">{convDisplayName(conv)}</div>
            {typingUserId ? (
              <span className="im-typing-indicator">
                <span className="im-typing-dots"><span /><span /><span /></span>
                正在输入…
              </span>
            ) : (conv.type === 'group' || conv.type === 'team_group') ? (
              <span className="im-chat-header-sub">{conv.type}</span>
            ) : null}
          </div>
        </div>
        <div className="im-chat-header-actions">
          <button className="im-icon-btn" title="搜索" onClick={() => setShowInConvSearch((v) => !v)}>
            <IconSearch size={18} />
          </button>
          <button className="im-icon-btn" title="详情" onClick={onToggleDetail}>
            <IconDots size={18} />
          </button>
        </div>
      </div>
      {showInConvSearch && <InConvSearchBar convId={conv.id} onClose={() => setShowInConvSearch(false)} />}

      {/* commit 6 顶部 Tab 栏 — 消息/文件/图片/Pin */}
      <div className="im-chat-tabs">
        <div className={`im-chat-tab${topTab === 'messages' ? ' active' : ''}`} onClick={() => setTopTab('messages')}>消息</div>
        <div className={`im-chat-tab${topTab === 'files' ? ' active' : ''}`} onClick={() => setTopTab('files')}>文件</div>
        <div className={`im-chat-tab${topTab === 'images' ? ' active' : ''}`} onClick={() => setTopTab('images')}>图片</div>
        <div className={`im-chat-tab${topTab === 'pin' ? ' active' : ''}`} onClick={() => setTopTab('pin')}>
          <IconPin size={13} style={{ verticalAlign: '-2px' }} /> Pin{pins && pins.length > 0 ? ` (${pins.length})` : ''}
        </div>
      </div>

      {conv.type === 'group' && pins && pins.length > 0 && (
        <div className="im-announcement">
          <IconPin size={14} /> <strong>置顶：</strong>{(pins[0].content ?? '').slice(0, 80)}
          {pins.length > 1 && <span style={{ marginLeft: 8, color: 'var(--im-text-tertiary)' }}>+{pins.length - 1} 条</span>}
        </div>
      )}

      {/* P1-6 多选模式条 */}
      {selectMode && (
        <div className="im-multi-select-bar">
          <span>已选 {selectedIds.size} 条</span>
          <button onClick={() => batchForward(Array.from(selectedIds))} disabled={selectedIds.size === 0}>批量转发</button>
          <button onClick={() => batchDelete(Array.from(selectedIds))} disabled={selectedIds.size === 0}>批量删除</button>
          <button style={{ background: 'var(--im-text-tertiary)' }}
                  onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}>退出多选</button>
        </div>
      )}
      {/* 消息列表 */}
      <div
        ref={listRef}
        className="im-msg-list"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onScroll={handleScroll}
        style={{ position: 'relative' }}
      >
        {dragOver && (
          <div className="im-drop-overlay">📥 释放发送图片 / 文件</div>
        )}
        {topTab === 'messages' && renderMessages(messages ?? [], me, conv, {
          editingId, editingText,
          selectMode, selectedIds,
          translationByMsg,
          unreadAfterId: lastReadIdRef.current,
          onToggleSelect: (id: number) => {
            const next = new Set(selectedIds);
            if (next.has(id)) next.delete(id); else next.add(id);
            setSelectedIds(next);
          },
          onStartEdit: (m: Message) => { setEditingId(m.id); setEditingText(m.content); },
          onChangeEditText: setEditingText,
          onSaveEdit: () => editingId && editMutation.mutate({ id: editingId, content: editingText }),
          onCancelEdit: () => setEditingId(null),
          onRecall: (id: number) => recall.mutate(id),
          onDelete: (id: number) => { if (!delMsg.isPending && window.confirm('删除？仅本人侧不可见')) delMsg.mutate(id); },
          onQuote: (m: Message) => setQuotedMessage(m),
          onForward: (m: Message) => setForwardModal(m),
          onStar: (m: Message) => star.mutate({ id: m.id, on: !m.isStarred }),
          onPin: (id: number) => pin.mutate({ id, on: true }),
          onShowReads: (m: Message) => setReadsModal(m),
          onTranslate: (m: Message) => translateMutation.mutate({ id: m.id, lang: /[一-龥]/.test(m.content) ? 'en' : 'zh' }),
          onEnterSelect: (m: Message) => { setSelectMode(true); setSelectedIds(new Set([m.id])); },
          onReport: (m: Message) => setReportTarget(m),
        })}
        {/* commit 6 文件/图片/Pin Tab 简化视图 */}
        {topTab === 'files' && (
          <FilteredView messages={messages ?? []} kind="file" />
        )}
        {topTab === 'images' && (
          <FilteredView messages={messages ?? []} kind="image" />
        )}
        {topTab === 'pin' && (
          <PinnedView pins={pins ?? []} />
        )}
        {/* commit 6 新消息浮动按钮 */}
        {scrolledUp && newMsgFloat > 0 && (
          <button className="im-new-msg-float" onClick={scrollToBottomNow}>
            ↓ {newMsgFloat} 条新消息
          </button>
        )}
        {topTab === 'messages' && (messages ?? []).length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--im-text-tertiary)', fontSize: 13, marginTop: 80 }}>
            发送你的第一条消息吧
          </div>
        )}
      </div>

      {/* 输入区 */}
      <div className="im-input-area">
        <div className="im-input-toolbar">
          <button className="im-toolbar-btn" title="表情"><IconMoodSmile size={18} /></button>
          <button className="im-toolbar-btn" title="文件" onClick={() => fileInputRef.current?.click()}>
            <IconPaperclip size={18} />
          </button>
          <button className="im-toolbar-btn" title="发送图片" onClick={() => imgInputRef.current?.click()}>
            <IconPhoto size={18} />
          </button>
          <button className="im-toolbar-btn" title="@提及" onClick={() => { setText(text + '@'); setMentionOpen(true); }}>
            <IconAt size={18} />
          </button>
          <button className="im-toolbar-btn" title="# 业务关联" onClick={() => { setText(text + '#'); setEntityOpen(true); }}>
            <IconHash size={18} />
          </button>
          <button
            className="im-toolbar-btn"
            title="截图：按 Win+Shift+S（Mac: Cmd+Shift+4）截图后直接 Ctrl+V 粘贴"
            onClick={() => antdMsg.info({
              content: '按 Win+Shift+S 截图后，直接 Ctrl+V 粘贴到输入框即可发送',
              duration: 4,
            })}
          >
            <IconScreenshot size={18} />
          </button>
          {/* v1.5：语音 / 待办尚未上线，移除假按钮避免点击无反应误导（参考 user feedback 摆设原则） */}
          <input ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile.mutate(f); e.target.value = ''; }} />
          <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile.mutate(f); e.target.value = ''; }} />
        </div>

        {quotedMessage && (
          <div className="im-input-quote">
            <span style={{ color: 'var(--im-primary)' }}>↩ 引用 {quotedMessage.senderRealName}</span>
            <span className="im-input-quote-content">{(quotedMessage.content ?? '').slice(0, 60)}</span>
            <button className="im-input-quote-close" onClick={() => setQuotedMessage(null)}>×</button>
          </div>
        )}

        {mentionOpen && (
          <MentionPicker
            query={mentionQuery}
            convId={conv.id}
            convType={conv.type}
            onSelect={(u: any) => {
              const before = text.replace(/@[一-龥\w]*$/, '');
              // @all 不需要 #id 后缀 — MessageContent 正则单独识别
              const token = u.id === 'all' ? '@all ' : `@${u.realName}#${u.id} `;
              setText(`${before}${token}`);
              setMentionOpen(false); setMentionQuery('');
            }}
            onClose={() => setMentionOpen(false)}
          />
        )}
        {entityOpen && (
          <EntityPicker query={entityQuery} onSelect={(e: any) => {
            const before = text.replace(/#[一-龥\w]*$/, '');
            setText(`${before}#${e.label}@${e.type}:${e.id} `);
            setEntityOpen(false); setEntityQuery('');
          }} onClose={() => setEntityOpen(false)} />
        )}

        <div className="im-input-box">
          <MentionEditor
            value={text}
            onValueChange={onInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !mentionOpen && !entityOpen) {
                e.preventDefault(); handleSend();
              } else if (e.key === 'Enter' && e.shiftKey) {
                e.preventDefault();
                document.execCommand('insertText', false, '\n');
              }
            }}
            placeholder="输入消息，@ 提及 # 业务  Enter 发送 / Shift+Enter 换行"
          />
          {text.trim() ? (
            <button className="im-send-btn" onClick={handleSend} disabled={send.isPending} title="发送">
              <IconSend size={16} />
            </button>
          ) : (
            <span className="im-send-hint">Enter ↵</span>
          )}
        </div>
      </div>

      {readsModal && <ReadsModal message={readsModal} onClose={() => setReadsModal(null)} />}
      {forwardModal && (
        <ForwardModal
          message={forwardModal}
          batchIds={selectMode ? Array.from(selectedIds) : undefined}
          onClose={() => { setForwardModal(null); setSelectMode(false); setSelectedIds(new Set()); }}
        />
      )}
      {reportTarget && (
        <ReportModal
          message={reportTarget}
          onSubmit={(reason: string) => reportMutation.mutate({ id: reportTarget.id, reason })}
          onClose={() => setReportTarget(null)}
        />
      )}
    </section>
  );
}

/* ────────── 消息列表渲染（合并同人 + 时间分割 + 未读分割线） ────────── */
function renderMessages(messages: Message[], me: any, conv: Conversation, handlers: any): any {
  const out: any[] = [];
  let prev: Message | undefined;
  const unreadAfter = handlers.unreadAfterId ? Number(handlers.unreadAfterId) : null;
  let unreadDividerInserted = false;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    // commit 6 未读分割线 — 首条 id > unreadAfter 且非自己发的
    if (!unreadDividerInserted && unreadAfter != null
        && Number(m.id) > unreadAfter
        && Number(m.fromUserId) !== Number(me.id)) {
      out.push(
        <div key={`unread-${m.id}`} className="im-unread-divider">
          以下是新消息
        </div>
      );
      unreadDividerInserted = true;
    }
    // 时间分割（跨 30 分钟）
    if (prev) {
      const gap = new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime();
      const cross30Min = gap > 30 * 60 * 1000;
      const crossDay = !dayjs(m.createdAt).isSame(prev.createdAt, 'day');
      if (crossDay) {
        out.push(
          <div key={`d-${m.id}`} className="im-msg-divider">
            {fmtDate(m.createdAt)}
          </div>
        );
      } else if (cross30Min) {
        out.push(
          <div key={`t-${m.id}`} className="im-msg-divider">
            {fmtTime(m.createdAt)}
          </div>
        );
      }
    }
    // 是否与上一条同发送人 5 分钟内
    const samePrev = !!prev
      && Number(prev.fromUserId) === Number(m.fromUserId)
      && m.fromUserId !== null
      && (new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() < SAME_SENDER_WINDOW_MS);
    // 是否本组最后一条（看下一条）
    const next = messages[i + 1];
    const sameNext = !!next
      && Number(next.fromUserId) === Number(m.fromUserId)
      && m.fromUserId !== null
      && (new Date(next.createdAt).getTime() - new Date(m.createdAt).getTime() < SAME_SENDER_WINDOW_MS);
    const isLastInGroup = !sameNext;
    out.push(
      <MessageBubble
        key={m.id}
        m={m}
        isMe={Number(m.fromUserId) === Number(me.id)}
        isSystem={m.fromUserId === null}
        isAtMe={(m.mentionedUserIds ?? []).some((x) => Number(x) === Number(me.id))}
        samePrev={samePrev}
        isLastInGroup={isLastInGroup}
        allMessages={messages}
        conv={conv}
        meId={me.id}
        {...handlers}
      />
    );
    prev = m;
  }
  return out;
}

/* ────────── 消息气泡 ────────── */
function MessageBubble({
  m, isMe, isSystem, isAtMe, samePrev, isLastInGroup, allMessages, conv, meId,
  editingId, editingText, onStartEdit, onChangeEditText, onSaveEdit, onCancelEdit,
  onRecall, onDelete, onQuote, onForward, onStar, onPin, onShowReads,
  onTranslate, onEnterSelect, onReport,
  selectMode, selectedIds, onToggleSelect,
  translationByMsg,
}: any) {
  const quotedSrc = m.replyToMessageId
    ? allMessages.find((x: any) => Number(x.id) === Number(m.replyToMessageId))
    : null;
  const editing = editingId === m.id;
  // 任务 3/4：右键菜单打开状态（必须在所有 early-return 之前调，遵守 React hook 顺序）
  const [contextOpen, setContextOpen] = useState(false);
  const [sysContextOpen, setSysContextOpen] = useState(false);
  const nav = useNavigate();
  if (m.deletedAt) return null;

  if (m.recalledAt) {
    return (
      <div className="im-msg-system">
        {isMe ? '你撤回了一条消息' : `${m.senderRealName ?? '对方'} 撤回了一条消息`}
      </div>
    );
  }

  // 任务 4：系统/卡片消息的菜单 — 不含编辑/撤回/删除（语义错）
  // 但保留复制 / 转发 / 收藏 / 翻译 / 多选 / 引用
  const systemMenu: MenuProps['items'] = [
    { key: 'copy', label: '复制', onClick: () => navigator.clipboard.writeText(m.content ?? '') },
    { key: 'quote', label: '引用', onClick: () => onQuote(m) },
    { key: 'forward', label: '转发', onClick: () => onForward(m) },
    { key: 'star', label: m.isStarred ? '取消收藏' : '收藏', onClick: () => onStar(m) },
    { key: 'translate', label: '翻译', onClick: () => onTranslate(m) },
    { key: 'select', label: '多选', onClick: () => onEnterSelect(m) },
  ];

  if (isSystem) {
    return (
      <div className="im-msg-row" data-msg-id={m.id}>
        <div className="im-msg-avatar-slot hidden" />
        <Dropdown
          menu={{ items: systemMenu }}
          trigger={['contextMenu']}
          overlayStyle={{ minWidth: 160 }}
          onOpenChange={setSysContextOpen}
        >
        <div className="im-msg-content"
             style={{ WebkitUserSelect: 'text', WebkitTouchCallout: 'none' } as any}>
          <div className={`im-bubble other${sysContextOpen ? ' context-active' : ''}`}
               style={{ background: '#FFF7E6' }}>
            <div>{m.content}</div>
            {m.quickActionsJson && m.quickActionsJson.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {m.quickActionsJson.map((a: any) => (
                  <Btn key={a.id} kind={(a.kind as any) ?? 'default'}
                       onClick={() => {
                         // 系统卡片按钮走前端跳转（quick-action 后端端点是空壳、不返回路由）
                         if (a.id === 'review' || m.linkedEntityType === 'approval') { nav('/approvals'); return; }
                         // P0-2：创建客户申请卡片——同意/拒绝原落到空壳 quick-action(不执行业务)→客户建不出来。
                         //   方案A止血：跳到审批页 /manager/customer-creation-requests(已验证可用)处理，不再走空壳。
                         if (m.linkedEntityType === 'customer_creation_request') { nav('/manager/customer-creation-requests'); return; }
                         // 新客户分配卡片：查看客户 / 立即跟进，用消息上的 linkedEntityId（customer）
                         if (m.linkedEntityType === 'customer' && m.linkedEntityId) {
                           if (a.id === 'view') { nav(`/customers/${m.linkedEntityId}`); return; }
                           if (a.id === 'followup') { nav(`/follow-ups/new?customer=${m.linkedEntityId}`); return; }
                         }
                         $.post(`/im/messages/${m.id}/quick-action`, { action_id: a.id });
                       }}>
                    {a.label}
                  </Btn>
                ))}
              </div>
            )}
          </div>
          <div className={`im-msg-meta`}>
            {fmtTime(m.createdAt)}
          </div>
        </div>
        </Dropdown>
      </div>
    );
  }

  // 文本消息菜单 — spec 2.8 完整 9 项
  const menu: MenuProps['items'] = [
    { key: 'copy', label: '复制', onClick: () => navigator.clipboard.writeText(m.content ?? '') },
    { key: 'quote', label: '引用', onClick: () => onQuote(m) },
    { key: 'forward', label: '转发', onClick: () => onForward(m) },
    { key: 'star', label: m.isStarred ? '取消收藏' : '收藏', onClick: () => onStar(m) },
    { key: 'translate', label: '翻译', onClick: () => onTranslate(m) },
    { key: 'select', label: '多选', onClick: () => onEnterSelect(m) },
    ...(conv.type === 'group' && ['owner', 'admin'].includes(conv.my_role)
      ? [{ key: 'pin', label: '群置顶', onClick: () => onPin(m.id) }] : []),
    ...(isMe ? [
      { key: 'edit', label: '编辑', disabled: Date.now() - new Date(m.createdAt).getTime() > 5 * 60 * 1000,
        onClick: () => onStartEdit(m) },
      { key: 'recall', label: '撤回', disabled: Date.now() - new Date(m.createdAt).getTime() > 2 * 60 * 1000,
        onClick: () => onRecall(m.id) },
      { key: 'delete', label: '删除', danger: true, onClick: () => onDelete(m.id) },
    ] : [
      { key: 'report', label: '举报', danger: true, onClick: () => onReport(m) },
    ]),
  ];

  return (
    <div className={`im-msg-row${samePrev ? ' same-prev' : ''}${isMe ? ' me' : ''}`}
         data-msg-id={m.id}
         style={isAtMe ? { background: 'rgba(255,125,0,0.04)', padding: 4, borderRadius: 8, marginLeft: -4, marginRight: -4 } : undefined}>
      {selectMode && (
        <input
          type="checkbox"
          className="im-msg-checkbox"
          checked={selectedIds?.has(m.id)}
          onChange={() => onToggleSelect?.(m.id)}
        />
      )}
      {!isMe && (
        <div className={`im-msg-avatar-slot${samePrev ? ' hidden' : ''}`}>
          {!samePrev && <Avatar userId={m.fromUserId ?? 0} src={m.senderAvatarUrl} name={m.senderRealName ?? '?'} size="md" />}
        </div>
      )}
      <Dropdown
        menu={{ items: menu }}
        trigger={['contextMenu']}
        overlayStyle={{ minWidth: 160 }}
        disabled={editing}
        onOpenChange={setContextOpen}
      >
      <div className={`im-msg-content${isMe ? ' me' : ''}`}
           style={{ WebkitUserSelect: 'text', WebkitTouchCallout: 'none' } as any}>
        {!samePrev && !isMe && (
          <div className="im-msg-name">{m.senderRealName}</div>
        )}
        <div className={`im-bubble ${isMe ? 'me' : 'other'}${m.contentType === 'image' ? ' image-msg' : ''}${m.contentType === 'file' ? ' file-msg' : ''}${contextOpen ? ' context-active' : ''}`}>
          {quotedSrc && (
            <div className="im-quote">
              <span className="im-quote-author">{quotedSrc.senderRealName}</span>:
              {' '}<MessageContent content={(quotedSrc.content ?? '').slice(0, 120)} meId={meId} />
            </div>
          )}
          {m.contentType === 'image' && m.attachment ? (
            <AntdImage src={m.attachment.fileUrl}
                       alt={m.attachment.fileName ?? '图片'}
                       style={{ maxWidth: 320, maxHeight: 320, borderRadius: 12, objectFit: 'cover' }} />
          ) : m.contentType === 'file' && m.attachment ? (
            isVoiceAtt(m.attachment) ? <VoiceBubble att={m.attachment} /> : <FileBubble att={m.attachment} />
          ) : editing ? (
            <div>
              <textarea
                className="im-input-textarea" autoFocus
                style={{ minHeight: 60, padding: 0, color: isMe ? '#fff' : 'inherit' }}
                value={editingText}
                onChange={(e) => onChangeEditText(e.target.value)}
              />
              <div className="im-edit-actions">
                <button onClick={onCancelEdit}>取消</button>
                <button className="primary" onClick={onSaveEdit}>保存</button>
              </div>
            </div>
          ) : (
            <>
              <MessageContent content={m.content} meId={meId} />
              {m.editedAt && <span className="im-msg-edited">（已编辑）</span>}
            </>
          )}
        </div>
        {translationByMsg?.[m.id] && (
          <div className="im-translation">
            <span className="im-translation-tag">翻译</span>
            {translationByMsg[m.id]}
          </div>
        )}
        {isLastInGroup && (
          <div className={`im-msg-meta${isMe ? ' me' : ''}`}>
            {m.isStarred && <span>⭐</span>}
            <span>{fmtTime(m.createdAt)}</span>
            {isMe && conv.type === 'direct' && (
              m.deliveryStatus === 'failed' ? <IconAlertCircle size={12} className="status-icon failed" />
              : m.deliveryStatus === 'read' ? <IconChecks size={12} className="status-icon read" />
              : m.deliveryStatus === 'delivered' ? <IconChecks size={12} className="status-icon" />
              : m.deliveryStatus === 'sent' ? <IconCheck size={12} className="status-icon" />
              : <IconClock size={12} className="status-icon" />
            )}
            {isMe && (conv.type === 'group' || conv.type === 'team_group') && (
              <span onClick={() => onShowReads(m)} style={{ cursor: 'pointer' }}>查看已读</span>
            )}
          </div>
        )}
      </div>
      </Dropdown>
      {/* 任务 3：删除 hover ↩/⋮ 浮条 — 右键菜单已是主路径，浮条多余 */}
    </div>
  );
}

/* ────────── @ # chip 内容渲染 ────────── */
function MessageContent({ content, meId }: { content: string; meId: number }) {
  const parts: any[] = [];
  let lastIdx = 0;
  // 三类 token：
  //   1) @张三#5   (picker 插入的标准 mention，带 user id)
  //   2) @all      (全员提及)
  //   3) #客户·张三@customer:7  (业务实体 chip)
  const re = /(@all\b)|(@[^@#\s]+#\d+)|(#[^@#\s]+@\w+:\d+)/g;
  let m: any; let key = 0;
  while ((m = re.exec(content)) !== null) {
    if (m.index > lastIdx) parts.push(content.slice(lastIdx, m.index));
    const tok = m[0];
    if (tok === '@all') {
      parts.push(
        <span key={key++} className="im-mention at-all">@所有人</span>
      );
    } else if (tok.startsWith('@')) {
      const mm = tok.match(/@([^#]+)#(\d+)/);
      const name = mm?.[1]; const id = Number(mm?.[2]);
      const isMe = id === Number(meId);
      parts.push(
        <span key={key++} className={`im-mention${isMe ? ' me' : ''}`}>
          @{name}
        </span>
      );
    } else {
      const mm = tok.match(/#([^@]+)@(\w+):(\d+)/);
      const label = mm?.[1]; const type = mm?.[2]; const id = mm?.[3];
      const url = type === 'customer' ? `/customers/${id}`
                : type === 'order' ? `/orders?id=${id}`
                : type === 'contract' ? `/contracts?id=${id}`
                : type === 'invoice' ? `/invoices?id=${id}` : '#';
      const typeLabel = type === 'customer' ? '客户'
                      : type === 'order' ? '订单'
                      : type === 'contract' ? '合同'
                      : type === 'invoice' ? '发票' : type;
      parts.push(
        <a key={key++} className="im-biz" href={url} target="_blank" rel="noreferrer">
          #{typeLabel}·{label}
        </a>
      );
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < content.length) parts.push(content.slice(lastIdx));
  return <>{parts}</>;
}

/* ────────── 文件卡片 ────────── */
function FileBubble({ att }: { att: Attachment }) {
  const ext = (att.fileName ?? '').split('.').pop()?.toLowerCase() ?? 'file';
  const icon = ['pdf'].includes(ext) ? '📕'
             : ['doc', 'docx'].includes(ext) ? '📘'
             : ['xls', 'xlsx'].includes(ext) ? '📗'
             : ['ppt', 'pptx'].includes(ext) ? '📙'
             : ['zip', 'rar', '7z'].includes(ext) ? '🗜️'
             : '📄';
  return (
    <a href={att.fileUrl} target="_blank" rel="noreferrer" className="im-file-card">
      <div className="im-file-icon">{icon}</div>
      <div className="im-file-info">
        <div className="im-file-name">{att.fileName ?? '文件'}</div>
        <div className="im-file-size">{att.fileSize ? `${(att.fileSize / 1024).toFixed(1)} KB` : ''}</div>
      </div>
    </a>
  );
}

/* ────────── 语音条（原生 <audio> 播 m4a；同会话同时只播一条；品牌蓝胶囊对齐手机端）────────── */
let CURRENT_VOICE_AUDIO: HTMLAudioElement | null = null;
function VoiceBubble({ att }: { att: Attachment }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);
  const [secs, setSecs] = useState<number | null>(parseVoiceSeconds(att.fileName));
  // 卸载（切换/关闭会话）时停播并清单例引用——detach 的 <audio> 不会自动暂停，否则离开会话后语音仍在响
  useEffect(() => () => {
    const a = audioRef.current;
    if (a) { a.pause(); if (CURRENT_VOICE_AUDIO === a) CURRENT_VOICE_AUDIO = null; }
  }, []);
  const widthPx = secs != null ? Math.min(240, 96 + secs * 6) : 128; // 时长越长条越宽（上限 240）
  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const a = audioRef.current;
        if (!a) return;
        if (a.paused) {
          if (CURRENT_VOICE_AUDIO && CURRENT_VOICE_AUDIO !== a) CURRENT_VOICE_AUDIO.pause(); // 同会话单播：播新的先停旧的
          CURRENT_VOICE_AUDIO = a;
          void a.play().catch(() => {}); // 浏览器自动播放策略兜底
        } else {
          a.pause();
        }
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none',
        padding: '8px 12px', borderRadius: 16, minWidth: 96, width: widthPx,
        background: 'var(--primary)', color: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }}
    >
      <span style={{
        flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.25)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {playing ? (
          <svg width="10" height="10" viewBox="0 0 10 10"><rect x="1" y="1" width="3" height="8" fill="#fff" /><rect x="6" y="1" width="3" height="8" fill="#fff" /></svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 1 L9 5 L2 9 Z" fill="#fff" /></svg>
        )}
      </span>
      <span style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.3)', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.round(pct * 100)}%`, background: '#fff', borderRadius: 2 }} />
      </span>
      <span style={{ flexShrink: 0, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{secs != null ? `${secs}"` : '语音'}</span>
      <audio
        ref={audioRef}
        src={att.fileUrl}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setPct(0); }}
        onTimeUpdate={(e) => { const a = e.currentTarget; if (a.duration && isFinite(a.duration)) setPct(a.currentTime / a.duration); }}
        onLoadedMetadata={(e) => { const d = e.currentTarget.duration; if (secs == null && isFinite(d)) setSecs(Math.round(d)); }}
      />
    </div>
  );
}

/* ────────── @mention 输入框（contentEditable 隐藏 #userId） ────────── */
const MENTION_TOKEN_RE = /@([^@#\s]+)#(\d+)|@all(?![一-龥\w])/g;

function MentionEditor({ value, onValueChange, onKeyDown, onPaste, placeholder }: {
  value: string;
  onValueChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastValueRef = useRef<string>('');

  const serialize = (el: HTMLElement): string => {
    let out = '';
    el.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) {
        out += n.textContent ?? '';
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        const e = n as HTMLElement;
        if (e.tagName === 'BR') out += '\n';
        else if (e.classList.contains('im-mention-chip')) {
          out += e.getAttribute('data-token') ?? '';
        } else {
          out += serialize(e);
        }
      }
    });
    return out.replace(/​/g, '');
  };

  const render = (el: HTMLDivElement, v: string) => {
    el.innerHTML = '';
    const re = new RegExp(MENTION_TOKEN_RE.source, 'g');
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(v))) {
      if (m.index > last) {
        el.appendChild(document.createTextNode(v.slice(last, m.index)));
      }
      const chip = document.createElement('span');
      chip.className = 'im-mention-chip';
      chip.setAttribute('contenteditable', 'false');
      chip.setAttribute('data-token', m[0]);
      chip.textContent = m[1] ? `@${m[1]}` : '@all';
      el.appendChild(chip);
      last = m.index + m[0].length;
    }
    if (last < v.length) {
      el.appendChild(document.createTextNode(v.slice(last)));
    }
    // 若末尾是 chip，附 ZWS 让光标可落在 chip 之后
    const lc = el.lastChild as HTMLElement | null;
    if (lc && lc.nodeType === Node.ELEMENT_NODE && lc.classList?.contains('im-mention-chip')) {
      el.appendChild(document.createTextNode('​'));
    }
  };

  const placeCursorAtEnd = (el: HTMLElement) => {
    const sel = window.getSelection();
    if (!sel) return;
    const r = document.createRange();
    r.selectNodeContents(el);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
  };

  useEffect(() => {
    if (!ref.current) return;
    if (lastValueRef.current === value) return;
    const wasFocused = document.activeElement === ref.current;
    render(ref.current, value);
    lastValueRef.current = value;
    if (wasFocused || value) placeCursorAtEnd(ref.current);
  }, [value]);

  const handleInput = () => {
    if (!ref.current) return;
    const v = serialize(ref.current);
    lastValueRef.current = v;
    onValueChange(v);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (onPaste) { onPaste(e); if (e.defaultPrevented) return; }
    e.preventDefault();
    const txt = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, txt);
  };

  return (
    <div
      ref={ref}
      className="im-input-textarea im-input-editor"
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onKeyDown={onKeyDown}
      onPaste={handlePaste}
      data-placeholder={placeholder}
    />
  );
}

/* ────────── @ picker / # picker ────────── */
function MentionPicker({ query, onSelect, convId, convType }: any) {
  // P1-8：群聊用本群成员；私聊/系统通知回退到 mention-candidates 全员
  const isGroup = ['group', 'team_group', 'company_broadcast'].includes(convType);
  const { data: candidates } = useQuery<any[]>({
    queryKey: ['im-mention', convId, isGroup, query],
    queryFn: () => isGroup
      ? $.get(`/im/conversations/${convId}/members-for-mention?q=${encodeURIComponent(query)}`)
      : $.get(`/im/mention-candidates?q=${encodeURIComponent(query)}`),
  });
  return (
    <div className="im-picker">
      {isGroup && (
        <div className="im-picker-item" onClick={() => onSelect({ id: 'all', realName: 'all' })}
             style={{ borderBottom: '0.5px solid var(--im-border-light)' }}>
          <span className="im-mention at-all" style={{ marginRight: 4 }}>@all</span>
          <span className="im-picker-name">所有人</span>
          <span className="im-picker-sub">仅管理员可用</span>
        </div>
      )}
      {(candidates ?? []).length === 0 && (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--im-text-tertiary)' }}>无匹配成员</div>
      )}
      {(candidates ?? []).map((u: any) => (
        <div key={u.id} className="im-picker-item" onClick={() => onSelect(u)}>
          <Avatar userId={u.id} src={u.avatarUrl} name={u.realName} size="sm" />
          <span className="im-picker-name">{u.realName}</span>
          <span className="im-picker-sub">{u.role}</span>
        </div>
      ))}
    </div>
  );
}
function EntityPicker({ query, onSelect }: any) {
  const [tab, setTab] = useState<'customer' | 'order' | 'contract' | 'invoice'>('customer');
  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['entity-search', tab, query],
    queryFn: async () => {
      const q = encodeURIComponent(query ?? '');
      if (tab === 'customer') {
        const r = await $.get<any>(`/customers?q=${q}&pageSize=10`);
        return (r.items ?? []).map((c: any) => ({
          id: c.id, label: c.realName ?? c.nickname ?? `#${c.id}`, type: 'customer',
          sub: c.currentStage ?? '',
        }));
      }
      if (tab === 'order') {
        const r = await $.get<any>(`/orders?q=${q}&pageSize=10`).catch(() => ({ items: [] }));
        return (r.items ?? []).map((o: any) => ({
          id: o.id, label: o.orderNo ?? `#${o.id}`, type: 'order',
          sub: `¥${Number(o.amount ?? 0).toLocaleString()}`,
        }));
      }
      if (tab === 'contract') {
        const r = await $.get<any>(`/contracts?q=${q}&pageSize=10`).catch(() => ({ items: [] }));
        return (r.items ?? []).map((o: any) => ({
          id: o.id, label: o.contractNo ?? `#${o.id}`, type: 'contract',
          sub: o.status ?? '',
        }));
      }
      if (tab === 'invoice') {
        const r = await $.get<any>(`/invoices?q=${q}&pageSize=10`).catch(() => ({ items: [] }));
        return (r.items ?? []).map((o: any) => ({
          id: o.id, label: o.invoiceNo ?? `#${o.id}`, type: 'invoice',
          sub: `¥${Number(o.amount ?? 0).toLocaleString()}`,
        }));
      }
      return [];
    },
  });
  return (
    <div className="im-picker">
      <div style={{ display: 'flex', gap: 4, padding: '8px 10px', borderBottom: '0.5px solid var(--im-border-light)' }}>
        {(['customer', 'order', 'contract', 'invoice'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '4px 12px', fontSize: 12, border: 'none', cursor: 'pointer',
            background: tab === t ? 'var(--im-primary)' : 'transparent',
            color: tab === t ? '#fff' : 'var(--im-text-secondary)', borderRadius: 4,
            transition: 'background 100ms linear',
          }}>
            {t === 'customer' ? '客户' : t === 'order' ? '订单' : t === 'contract' ? '合同' : '发票'}
          </button>
        ))}
      </div>
      {isLoading && (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--im-text-tertiary)' }}>加载中…</div>
      )}
      {!isLoading && (data ?? []).length === 0 && (
        <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--im-text-tertiary)' }}>
          {query ? '无匹配' : '暂无数据'}
        </div>
      )}
      {(data ?? []).map((e: any) => (
        <div key={`${e.type}-${e.id}`} className="im-picker-item" onClick={() => onSelect(e)}>
          <span className="im-biz" style={{ flexShrink: 0 }}>#</span>
          <span className="im-picker-name">{e.label}</span>
          {e.sub && <span style={{ fontSize: 11, color: 'var(--im-text-tertiary)' }}>{e.sub}</span>}
          <span className="im-picker-sub">#{e.id}</span>
        </div>
      ))}
    </div>
  );
}

/* ────────── 会话内搜索（高亮 + 上下导航） ────────── */
function InConvSearchBar({ convId, onClose }: { convId: number; onClose: () => void }) {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const { data } = useQuery<any[]>({
    queryKey: ['inconv-search', convId, q],
    queryFn: () => $.get(`/im/search?q=${encodeURIComponent(q)}&convId=${convId}`),
    enabled: q.length > 1,
  });
  const matches = data ?? [];

  // 给匹配消息加 class（高亮）+ 滚动到当前
  useEffect(() => {
    document.querySelectorAll('.im-msg-search-match, .im-msg-search-current').forEach((el) => {
      el.classList.remove('im-msg-search-match', 'im-msg-search-current');
    });
    matches.forEach((r: any, i: number) => {
      const el = document.querySelector(`[data-msg-id="${r.id}"]`);
      if (el) {
        el.classList.add('im-msg-search-match');
        if (i === cursor) el.classList.add('im-msg-search-current');
      }
    });
    if (matches[cursor]) {
      const el = document.querySelector(`[data-msg-id="${matches[cursor].id}"]`) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' });
    }
    return () => {
      document.querySelectorAll('.im-msg-search-match, .im-msg-search-current').forEach((el) => {
        el.classList.remove('im-msg-search-match', 'im-msg-search-current');
      });
    };
  }, [matches, cursor]);

  const next = () => setCursor((c) => Math.min(matches.length - 1, c + 1));
  const prev = () => setCursor((c) => Math.max(0, c - 1));

  return (
    <div style={{
      padding: '8px 16px', borderBottom: '0.5px solid var(--im-border-light)',
      background: 'var(--im-bg-chat)', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <Input value={q}
             onChange={(e) => { setQ(e.target.value); setCursor(0); }}
             onKeyDown={(e) => {
               if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); next(); }
               if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); prev(); }
             }}
             placeholder="搜索本会话消息（Enter 下一个 / Shift+Enter 上一个）"
             size="small" style={{ flex: 1 }} autoFocus />
      <span style={{ fontSize: 11, color: 'var(--im-text-tertiary)', minWidth: 60, textAlign: 'right' }}>
        {matches.length > 0 ? `${cursor + 1} / ${matches.length}` : `${matches.length} 条`}
      </span>
      <button className="im-icon-btn" onClick={prev} disabled={matches.length === 0} title="上一个">↑</button>
      <button className="im-icon-btn" onClick={next} disabled={matches.length === 0} title="下一个">↓</button>
      <button className="im-icon-btn" onClick={onClose}><IconX size={14} /></button>
    </div>
  );
}

/* ────────── 全局搜索 ────────── */
function GlobalSearchModal({ open, onClose, onJump }: any) {
  const [q, setQ] = useState('');
  const { data } = useQuery<any[]>({
    queryKey: ['global-search', q],
    queryFn: () => $.get(`/im/search?q=${encodeURIComponent(q)}`),
    enabled: open && q.length > 1,
  });
  return (
    <Modal title={<span><IconSearch size={15} style={{ verticalAlign: '-2px' }} /> 搜索消息</span>} open={open} onCancel={onClose} footer={null} width={640}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="搜索全部会话的消息..." />
      <div style={{ maxHeight: 400, overflowY: 'auto', marginTop: 12 }}>
        {(data ?? []).length === 0 && q.length > 1 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--im-text-tertiary)' }}>无匹配</div>
        )}
        {(data ?? []).map((r: any) => (
          <div key={r.id} onClick={() => onJump(Number(r.convId), Number(r.id))} className="im-picker-item"
               style={{ borderBottom: '0.5px solid var(--im-border-light)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 500, fontSize: 13 }}>{r.senderRealName ?? '系统'} · {r.convName ?? r.convType}</span>
                <span style={{ fontSize: 11, color: 'var(--im-text-tertiary)' }}>{fmtMonthDayTime(r.created_at)}</span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--im-text-secondary)' }}>{highlightMatch(r.content, q)}</div>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
function highlightMatch(content: string, q: string): any {
  if (!q) return content;
  const idx = content.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return content;
  return (
    <>
      {content.slice(Math.max(0, idx - 20), idx)}
      <mark style={{ background: 'var(--im-warning)', color: '#fff', padding: '0 2px' }}>
        {content.slice(idx, idx + q.length)}
      </mark>
      {content.slice(idx + q.length, idx + q.length + 60)}
    </>
  );
}

/* ────────── 转发（支持单条 + 多选批量） ────────── */
function ForwardModal({ message, batchIds, onClose }:
  { message: Message; batchIds?: number[]; onClose: () => void }) {
  const { data: convs } = useQuery<any[]>({
    queryKey: ['im-conversations'], queryFn: () => $.get('/im/conversations'),
  });
  const [selected, setSelected] = useState<number[]>([]);
  const ids = batchIds && batchIds.length > 0 ? batchIds : [message.id];
  const send = useMutation({
    mutationFn: async () => {
      let total = 0;
      for (const mid of ids) {
        const r: any = await $.post(`/im/messages/${mid}/forward`, { conv_ids: selected });
        total += r?.forwarded ?? 0;
      }
      return { forwarded: total };
    },
    onSuccess: (r: any) => { antdMsg.success(`已转发 ${ids.length} 条 → ${r.forwarded} 次`); onClose(); },
    onError: (e: any) => antdMsg.error(e?.message ?? '转发失败'),
  });
  return (
    <Modal title={ids.length > 1 ? `批量转发（${ids.length} 条）` : '转发消息'}
           open onCancel={onClose} footer={null} width={520}>
      <div style={{ padding: 10, background: 'var(--im-bg-bubble-other)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
        {ids.length > 1 ? `${ids.length} 条消息` : (message.contentType === 'image' ? '[图片]' : message.content.slice(0, 100))}
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {(convs ?? []).filter((c: any) => c.id !== message.conversationId).map((c: any) => {
          const sel = selected.includes(c.id);
          return (
            <div key={c.id} className="im-picker-item"
                 onClick={() => setSelected(sel ? selected.filter((x) => x !== c.id) : [...selected, c.id])}>
              <input type="checkbox" checked={sel} readOnly />
              <span style={{ fontSize: 14 }}>{c.name}</span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Btn kind="primary" onClick={() => send.mutate()} disabled={selected.length === 0 || send.isPending}>
          {send.isPending ? '转发中…' : `转发到 ${selected.length} 个会话`}
        </Btn>
      </div>
    </Modal>
  );
}

/* ────────── 举报 ────────── */
function ReportModal({ message, onSubmit, onClose }:
  { message: Message; onSubmit: (reason: string) => void; onClose: () => void }) {
  const [reason, setReason] = useState('');
  const presets = ['垃圾广告', '骚扰恐吓', '违法违规', '其他'];
  return (
    <Modal title="🚩 举报消息" open onCancel={onClose} footer={null} width={420}>
      <div style={{ padding: 10, background: 'var(--im-bg-bubble-other)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
        {(message.content ?? '').slice(0, 200)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {presets.map((p) => (
          <button key={p} onClick={() => setReason(p)} style={{
            padding: '4px 12px', fontSize: 12, cursor: 'pointer',
            border: `1px solid ${reason === p ? 'var(--im-primary)' : 'var(--im-border-default)'}`,
            background: reason === p ? 'var(--im-mention-bg)' : '#fff',
            color: reason === p ? 'var(--im-primary)' : 'var(--im-text-secondary)',
            borderRadius: 16,
          }}>{p}</button>
        ))}
      </div>
      <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)}
                      rows={3} placeholder="详细描述（必填）" />
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Btn kind="primary" onClick={() => reason.trim() && onSubmit(reason)} disabled={!reason.trim()}>
          提交举报
        </Btn>
      </div>
    </Modal>
  );
}

/* ────────── 已读人数 ────────── */
function ReadsModal({ message, onClose }: { message: Message; onClose: () => void }) {
  const { data } = useQuery<any>({
    queryKey: ['msg-reads', message.id],
    queryFn: () => $.get(`/im/messages/${message.id}/reads`),
  });
  if (!data) return null;
  return (
    <Modal title="已读详情" open onCancel={onClose} footer={null} width={400}>
      <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--im-text-secondary)' }}>
        {data.readCount} / {data.totalRecipients} 人已读
      </div>
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {data.readers.map((r: any) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
            <Avatar userId={r.id} src={r.avatarUrl} name={r.realName} size="sm" />
            <span>{r.realName}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--im-text-tertiary)' }}>
              {fmtMonthDayTime(r.readAt)}
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

/* ────────── 详情抽屉 ────────── */
function DetailDrawer({ conv, me }: { conv: Conversation; me: any }) {
  const qc = useQueryClient();
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const { data: detail } = useQuery<any>({
    queryKey: ['im-conv-detail', conv.id],
    queryFn: () => $.get(`/im/conversations/${conv.id}`),
  });
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(conv.name);
  const [announcement, setAnnouncement] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/im/conversations/${conv.id}/avatar`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: () => {
      antdMsg.success('群头像已更新');
      qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] });
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
    },
    onError: (e: any) => antdMsg.error(e?.response?.data?.message ?? '上传失败'),
  });
  const removeAvatar = useMutation({
    mutationFn: () => api.delete(`/im/conversations/${conv.id}/avatar`).then((r) => r.data),
    onSuccess: () => {
      antdMsg.success('已移除');
      qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] });
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
    },
  });
  useEffect(() => {
    if (detail) { setName(detail.name ?? ''); setAnnouncement(detail.announcement ?? ''); }
  }, [detail]);
  const canManage = ['owner', 'admin'].includes(detail?.members?.find((m: any) => Number(m.userId) === Number(me.id))?.role);
  const update = useMutation({
    mutationFn: (dto: any) => api.patch(`/im/conversations/${conv.id}/settings`, dto).then((r) => r.data),
    onSuccess: () => {
      antdMsg.success('已保存'); setEditing(false);
      qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] });
      qc.invalidateQueries({ queryKey: ['im-conversations'] });
    },
  });
  const removeMember = useMutation({
    mutationFn: (uid: number) => api.delete(`/im/conversations/${conv.id}/members/${uid}`).then((r) => r.data),
    onSuccess: () => { antdMsg.success('已移除'); qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] }); },
  });
  const transferOwner = useMutation({
    mutationFn: (uid: number) => $.post(`/im/conversations/${conv.id}/transfer-owner`, { to_user_id: uid }),
    onSuccess: () => { antdMsg.success('已转让群主'); qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] }); },
  });
  // v1.8 项2 解散群（群主 / admin / director）
  const myRole = detail?.members?.find((m: any) => Number(m.userId) === Number(me.id))?.role;
  const canDissolveGroup = (conv.type === 'group' || conv.type === 'team_group')
    && (myRole === 'owner' || ['admin', 'director'].includes(me.role));
  const dissolveGroup = useMutation({
    mutationFn: () => $.post(`/im/conversations/${conv.id}/dissolve`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['im-conversations'] }); /* toast 统一由 WS conversation:dissolved 弹，避免重复 */ },
    onError: (e: any) => antdMsg.error(e?.message ?? '解散失败'),
  });
  const members: any[] = detail?.members ?? [];

  return (
    <aside className="im-detail-pane">
      <div className="im-detail-section">
        <div className="im-detail-label">会话信息</div>
        {/* P1-4 群头像 — admin 可上传，覆盖 AI 字 */}
        {(conv.type === 'group' || conv.type === 'team_group') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            {detail?.avatar ? (
              <img src={detail.avatar} alt="群头像"
                   style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover',
                            boxShadow: 'inset 0 0 0 1px var(--im-avatar-border)' }} />
            ) : (
              <div className={`im-avatar c${(detail?.avatarColorIndex ?? 1) % 8 + 1}`}
                   style={{ width: 64, height: 64, fontSize: detail?.avatarText?.length === 2 ? 22 : 28 }}>
                {detail?.avatarText ?? (detail?.name ?? '群').slice(0, 1)}
              </div>
            )}
            {canManage && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Btn kind="default" onClick={() => avatarFileRef.current?.click()}>
                  📷 {detail?.avatar ? '更换' : '上传'}头像
                </Btn>
                {detail?.avatar && (
                  <button onClick={() => { if (window.confirm('移除后将回到 AI 生成的字符头像')) removeAvatar.mutate(); }}
                          style={{ fontSize: 11, color: 'var(--im-text-tertiary)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    🗑️ 移除头像
                  </button>
                )}
                <input ref={avatarFileRef} type="file" accept="image/*"
                       style={{ display: 'none' }}
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         if (f) uploadAvatar.mutate(f);
                         e.target.value = '';
                       }} />
              </div>
            )}
          </div>
        )}
        {editing && canManage ? (
          <Input value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8 }} />
        ) : (
          <div className="im-detail-value" style={{ fontWeight: 500, fontSize: 16 }}>{detail?.name ?? '—'}</div>
        )}
        <div className="im-detail-label" style={{ marginTop: 8 }}>{conv.type}</div>
      </div>

      <div className="im-detail-section">
        <div className="im-detail-label"><IconSpeakerphone size={14} style={{ verticalAlign: '-2px' }} /> 群公告</div>
        {editing && canManage ? (
          <Input.TextArea value={announcement} onChange={(e) => setAnnouncement(e.target.value)} rows={3} style={{ marginBottom: 8 }} />
        ) : (
          <div style={{
            fontSize: 13, color: 'var(--im-text-secondary)', whiteSpace: 'pre-wrap',
            padding: 8, background: '#FFF7E6', borderRadius: 6,
          }}>{detail?.announcement || '（无公告）'}</div>
        )}
        {canManage && (
          editing ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Btn kind="primary" onClick={() => update.mutate({ name, announcement })}>保存</Btn>
              <Btn kind="default" onClick={() => setEditing(false)}>取消</Btn>
            </div>
          ) : (
            <Btn kind="default" onClick={() => setEditing(true)}>编辑群信息</Btn>
          )
        )}
        {canDissolveGroup && (
          <button
            onClick={() => { if (window.confirm('确认解散？群将被删除、全员移出，不可恢复')) dissolveGroup.mutate(); }}
            style={{
              marginTop: 10, padding: '5px 12px', fontSize: 12, cursor: 'pointer',
              border: '1px solid var(--danger, #ff4d4f)', borderRadius: 4,
              background: 'transparent', color: 'var(--danger, #ff4d4f)',
            }}>
            解散群聊
          </button>
        )}
      </div>

      <div className="im-detail-section">
        <div className="im-detail-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>成员（{members.length}）</span>
          {canManage && (conv.type === 'group' || conv.type === 'team_group') && (
            <button onClick={() => setShowInvite(true)}
              style={{ fontSize: 12, color: 'var(--im-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              ＋ 邀请成员
            </button>
          )}
        </div>
        {members.map((m: any) => {
          const isMe = Number(m.userId) === Number(me.id);
          const memberMenu: MenuProps['items'] = canManage && !isMe ? [
            ...(detail?.members.find((x: any) => Number(x.userId) === Number(me.id))?.role === 'owner'
              ? [{ key: 'transfer', label: '转让群主',
                  onClick: () => { if (window.confirm('确认转让？')) transferOwner.mutate(m.userId); } }] : []),
            { key: 'remove', label: '移除', danger: true,
              onClick: () => { if (window.confirm('移除该成员？')) removeMember.mutate(m.userId); } },
          ] : [];
          return (
            <Dropdown key={m.id} menu={{ items: memberMenu }} trigger={['contextMenu']} disabled={!canManage || isMe}>
              <div className="im-detail-member-row">
                <Avatar userId={m.userId} src={m.avatarUrl} name={m.realName} size="sm" />
                <span>{m.realName}{isMe && ' (我)'}</span>
                {m.role === 'owner' && <span className="im-role-badge owner">群主</span>}
                {m.role === 'admin' && <span className="im-role-badge admin">管理员</span>}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--im-text-tertiary)' }}>{m.userRole}</span>
              </div>
            </Dropdown>
          );
        })}
      </div>
      {showInvite && (
        <InviteMembersModal
          convId={Number(conv.id)}
          existingIds={members.map((m: any) => Number(m.userId))}
          onClose={() => setShowInvite(false)}
          onDone={() => {
            setShowInvite(false);
            qc.invalidateQueries({ queryKey: ['im-conv-detail', conv.id] });
            qc.invalidateQueries({ queryKey: ['im-conversations'] });
          }}
        />
      )}
    </aside>
  );
}

/* ────────── P0-2 邀请成员（复用 searchable users，排除已有成员） ────────── */
function InviteMembersModal({ convId, existingIds, onClose, onDone }: {
  convId: number; existingIds: number[]; onClose: () => void; onDone: () => void;
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const existing = new Set(existingIds.map(Number));
  const { data: users } = useQuery<any[]>({
    queryKey: ['im-searchable-users', query],
    queryFn: () => $.get(`/im/users/searchable?q=${encodeURIComponent(query)}`),
  });
  const candidates = (users ?? []).filter((u: any) => !existing.has(Number(u.id)));
  const invite = useMutation({
    mutationFn: () => api.post(`/im/conversations/${convId}/members`, { user_ids: selected }).then((r) => r.data),
    onSuccess: (r: any) => { antdMsg.success(`已邀请 ${r?.added ?? selected.length} 人`); onDone(); },
    onError: (e: any) => antdMsg.error(e?.response?.data?.message ?? e?.message ?? '邀请失败'),
  });
  return (
    <Modal title="邀请成员" open onCancel={onClose} footer={null} width={480}>
      <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="搜索同事姓名" style={{ marginBottom: 8 }} />
      <div style={{ maxHeight: 300, overflowY: 'auto', border: '0.5px solid var(--im-border-light)', borderRadius: 8 }}>
        {candidates.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', fontSize: 12, color: 'var(--im-text-tertiary)' }}>无可邀请的成员</div>
        )}
        {candidates.map((u: any) => {
          const sel = selected.includes(u.id);
          return (
            <div key={u.id} className="im-picker-item"
                 style={sel ? { background: 'var(--im-mention-bg)' } : undefined}
                 onClick={() => setSelected(sel ? selected.filter((x) => x !== u.id) : [...selected, u.id])}>
              <input type="checkbox" checked={sel} readOnly />
              <Avatar userId={u.id} src={u.avatarUrl} name={u.realName} size="sm" />
              <span>{u.realName}</span>
              <span style={{ fontSize: 11, color: 'var(--im-text-tertiary)', marginLeft: 'auto' }}>{u.role}</span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <Btn kind="primary" onClick={() => invite.mutate()} disabled={selected.length === 0 || invite.isPending}>
          {invite.isPending ? '邀请中…' : `邀请（${selected.length}）`}
        </Btn>
      </div>
    </Modal>
  );
}

function EmptyState() {
  return (
    <section className="im-chat-pane" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: 'var(--im-text-tertiary)', fontSize: 14, gap: 8 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 8, color: 'var(--im-text-tertiary)' }}><IconMessageCircle size={48} stroke={1.5} /></div>
        <div>选择一个会话开始聊天</div>
      </div>
    </section>
  );
}

/* ────────── commit 6 Tab 过滤视图 ────────── */
function FilteredView({ messages, kind }: { messages: Message[]; kind: 'file' | 'image' }) {
  const filtered = messages.filter((m) => m.contentType === kind && !m.recalledAt && !m.deletedAt);
  if (filtered.length === 0) {
    return <div style={{ textAlign: 'center', color: 'var(--im-text-tertiary)', fontSize: 13, marginTop: 80 }}>
      暂无{kind === 'image' ? '图片' : '文件'}
    </div>;
  }
  if (kind === 'image') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
        {filtered.map((m) => m.attachment?.fileUrl ? (
          <a key={m.id} href={m.attachment.fileUrl} target="_blank" rel="noreferrer">
            <img src={m.attachment.fileUrl} alt=""
                 style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 6 }} />
          </a>
        ) : null)}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {filtered.map((m) => m.attachment ? (
        <a key={m.id} href={m.attachment.fileUrl} target="_blank" rel="noreferrer"
           className="im-file-card" style={{ maxWidth: 480 }}>
          <div className="im-file-icon">📄</div>
          <div className="im-file-info">
            <div className="im-file-name">{m.attachment.fileName ?? '文件'}</div>
            <div className="im-file-size">
              {m.attachment.fileSize ? `${(m.attachment.fileSize / 1024).toFixed(1)} KB` : ''} ·
              {' '}{m.senderRealName ?? ''} {fmtMonthDayTime(m.createdAt)}
            </div>
          </div>
        </a>
      ) : null)}
    </div>
  );
}

function PinnedView({ pins }: { pins: any[] }) {
  if (pins.length === 0) {
    return <div style={{ textAlign: 'center', color: 'var(--im-text-tertiary)', fontSize: 13, marginTop: 80 }}>
      还没有 Pin 消息
    </div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {pins.map((p) => (
        <div key={p.pinId} style={{
          padding: 12, borderRadius: 8,
          background: '#FFF7E6', border: '1px solid rgba(255,125,0,0.2)',
        }}>
          <div style={{ fontSize: 12, color: 'var(--im-text-tertiary)', marginBottom: 4 }}>
            <IconPin size={11} style={{ verticalAlign: '-1px' }} /> {fmtMonthDayTime(p.pinned_at)}
          </div>
          <div style={{ fontSize: 14, color: 'var(--im-text-primary)' }}>{p.content}</div>
        </div>
      ))}
    </div>
  );
}

/* helper */
function extractMentionIds(text: string): number[] {
  const ids: number[] = [];
  const re = /@[^@#\s]+#(\d+)/g;
  let m;
  while ((m = re.exec(text)) !== null) ids.push(Number(m[1]));
  return ids;
}
